import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  EXAMPLE_COUNTS,
  exampleCountFrom,
  exampleNoteFrom,
  exampleNotes,
  examplesMessages,
  examplesReply,
  examplesVerdict,
  formatMessages,
  HISTORY_LIMIT,
  historyGrowth,
  historyMessages,
  historyRequestTokens,
  historyTransition,
  historyVerdict,
  initialExamples,
  initialHistory,
  initialInjection,
  initialParts,
  INJECTION_NOTES,
  injectionNoteFrom,
  injectionNoteLabels,
  INJECTION_RUNS,
  injectionReply,
  injectionTransition,
  injectionUserMessage,
  injectionVerdict,
  partSentences,
  partsReply,
  partsSystemMessage,
  partsVerdict,
  promptTokens,
  replyLines,
  replyFollowsInstruction,
  type HistoryAction,
  type InjectionAction,
  type PartName,
} from './prompt-models';

import './relationship-labs.css';
import './prompt-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
const SIMULATION = 'A simulation built from real runs.';
const exampleCountLabel = (count: number) =>
  count === 0 ? 'No examples' : count === 1 ? 'One example' : 'Two examples';

export function PromptPartsLab() {
  const [state, setState] = useState(initialParts);
  const toggle = (part: PartName) =>
    setState((current) => ({ ...current, [part]: !current[part] }));
  const system = partsSystemMessage(state);
  const reply = partsReply(state);
  const verdict = partsVerdict(state);
  return (
    <Lab
      title="What changes in the reply when the instruction changes?"
      eyebrow="Prompt model · the three parts"
      className="rel-lab prompt-lab"
    >
      <Predict>
        Will the heading “Apollo standup” come back as a task? Turn on Name the job, then add Set
        the shape and Set the rule.
      </Predict>
      <div className="cb-controls">
        <Toggle label="Name the job" checked={state.job} onChange={() => toggle('job')} />
        <Toggle label="Set the shape" checked={state.shape} onChange={() => toggle('shape')} />
        <Toggle label="Set the rule" checked={state.rule} onChange={() => toggle('rule')} />
        <Reset onClick={() => setState(initialParts())} />
      </div>
      <div className="cb-two">
        <Panel label="System message">
          <pre aria-label="System message">{system || '(empty)'}</pre>
        </Panel>
        <Panel label="Reply">
          <pre aria-label="Reply">{reply}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['System message sentences', String(partSentences(state).length)],
          ['Reply lines', String(replyLines(reply))],
        ]}
      />
      <Result tone={verdict.tone}>{verdict.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function PromptExamplesLab() {
  const [state, setState] = useState(initialExamples);
  const messages = examplesMessages(state);
  const reply = examplesReply(state);
  const verdict = examplesVerdict(state);
  return (
    <Lab
      title="Does one example teach the shape faster than a rule?"
      eyebrow="Prompt model · examples"
      className="rel-lab prompt-lab"
    >
      <Predict>
        Will the reply be exactly “No tasks” with no example? Set Examples to One example, then set
        Note to Note 2.
      </Predict>
      <div className="cb-controls">
        <label>
          Examples
          <ModelSelect
            aria-label="Examples"
            value={String(state.examples)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, examples: exampleCountFrom(value) }))
            }
            options={EXAMPLE_COUNTS.map((count) => ({
              value: String(count),
              label: exampleCountLabel(count),
            }))}
          />
        </label>
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: exampleNoteFrom(value) }))
            }
            options={exampleNotes.map((note) => ({
              value: String(note.id),
              label: `Note ${note.id}: ${note.title}`,
            }))}
          />
        </label>
        <Reset onClick={() => setState(initialExamples())} />
      </div>
      <div className="cb-two">
        <Panel label="Messages sent">
          <pre aria-label="Messages sent">{formatMessages(messages)}</pre>
        </Panel>
        <Panel label="Reply">
          <pre aria-label="Reply">{reply}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Messages', String(messages.length)],
          ['Prompt tokens', String(promptTokens(messages))],
        ]}
      />
      <Result tone={verdict.tone}>{verdict.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function PromptHistoryLab() {
  const [state, setState] = useState(initialHistory);
  const act = (action: HistoryAction) => setState((current) => historyTransition(current, action));
  const messages = historyMessages(state.turns);
  const verdict = historyVerdict(state);
  const sent = state.turns > 0;
  return (
    <Lab
      title="Why does a conversation grow more expensive each turn?"
      eyebrow="Prompt model · the history"
      className="rel-lab prompt-lab"
    >
      <Predict>
        How much of the fourth request is new, and how much is old turns resent? Press Ask the next
        follow-up four times.
      </Predict>
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={state.turns >= HISTORY_LIMIT}
          onClick={() => act({ type: 'next' })}
        >
          Ask the next follow-up
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Messages in the next request">
        <pre aria-label="Messages in the next request">{formatMessages(messages)}</pre>
      </Panel>
      <Facts
        values={[
          ['Turns so far', `${state.turns} of ${HISTORY_LIMIT}`],
          ...(sent
            ? ([
                ['Last request tokens', String(historyRequestTokens(state.turns))],
                ['Growth', historyGrowth(state.turns)],
              ] as [string, string][])
            : []),
        ]}
      />
      <Result tone={verdict.tone}>{verdict.message}</Result>
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function PromptInjectionLab() {
  const [state, setState] = useState(initialInjection);
  const act = (action: InjectionAction) =>
    setState((current) => injectionTransition(current, action));
  const reply = injectionReply(state);
  const verdict = injectionVerdict(state);
  return (
    <Lab
      title="What happens when the note contains an instruction?"
      eyebrow="Prompt model · a note that argues"
      className="rel-lab prompt-lab"
    >
      <Predict>
        Will the reply list the tasks or obey the sentence planted in the note? Choose the note with
        a fake system message, then press Run again twice.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={state.note}
            onValueChange={(value) => act({ type: 'note', value: injectionNoteFrom(value) })}
            options={INJECTION_NOTES.map((note) => ({
              value: note,
              label: injectionNoteLabels[note],
            }))}
          />
        </label>
        <Toggle
          label="Treat the note as data"
          checked={state.dataRule}
          onChange={() => act({ type: 'dataRule' })}
        />
        <Button size="sm" onClick={() => act({ type: 'again' })}>
          Run again
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="User message">
          <pre aria-label="User message">{injectionUserMessage(state)}</pre>
        </Panel>
        <Panel label="Reply">
          <pre aria-label="Reply">{reply}</pre>
        </Panel>
      </div>
      <Facts
        values={[
          ['Run', `${state.run} of ${INJECTION_RUNS}`],
          ['Reply follows the instruction', replyFollowsInstruction(reply) ? 'yes' : 'no'],
          ['Data rule', state.dataRule ? 'on' : 'off'],
        ]}
      />
      <Result tone={verdict.tone}>{verdict.message}</Result>
      <p className="rel-caption">
        Real runs, replayed here: another model or day gives different replies.
      </p>
    </Lab>
  );
}
